import Loader from 'components/Loader';
import useAuth from 'hooks/useAuth';
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getRoleRedirectPath } from 'utils/role-utils';

const getLoginPath = (allowedRoles?: string[]): string => {
  if (allowedRoles && (allowedRoles.includes('admin') || allowedRoles.includes('manager'))) {
    return '/auth/login/email';
  }
  return '/auth/login';
};

// ==============================|| ROLE-BASED REDIRECT ||============================== //

interface RoleBasedRedirectProps {
  fallbackPath?: string;
  allowedRoles?: string[];
  customRedirects?: Record<string, string>;
  rememberLastPath?: boolean;
}

export default function RoleBasedRedirect({
  fallbackPath,
  allowedRoles,
  customRedirects = {},
  rememberLastPath = false
}: RoleBasedRedirectProps) {
  const { isLoggedIn, user, isInitialized } = useAuth();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isInitialized !== undefined) {
      setIsLoading(false);
    }
  }, [isInitialized]);

  if (isLoading) {
    return <Loader />;
  }

  if (!isLoggedIn) {
    const loginPath = getLoginPath(allowedRoles);
    return <Navigate to={loginPath} replace />;
  }

  if (!user?.role) {
    const loginPath = getLoginPath(allowedRoles);
    return <Navigate to={loginPath} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    const redirectPath = fallbackPath || getRoleRedirectPath(user.role);
    return <Navigate to={redirectPath} replace />;
  }

  if (customRedirects[user.role]) {
    return <Navigate to={customRedirects[user.role]} replace />;
  }

  if (rememberLastPath) {
    const lastPath = localStorage.getItem('lastVisitedPath');
    if (lastPath && lastPath !== location.pathname) {
      localStorage.removeItem('lastVisitedPath');
      return <Navigate to={lastPath} replace />;
    }
  }

  const redirectPath = getRoleRedirectPath(user.role);

  return <Navigate to={redirectPath} replace />;
}
